/**
 * Admin auth contract — keep in sync with softcar-backend:
 * - `src/app/utils/authCookieNames.ts` (`AUTH_PLATFORM_HEADER`, admin cookie names)
 * - `src/app/utils/extractToken.ts` (Bearer, then audience cookies)
 * - `src/app/modules/global/auth/controllers/*` (login / refresh / logout)
 *
 * The admin SPA does not read JWTs in JS; the API sets HttpOnly `softcar_admin_*` cookies.
 * Every request must send `x-softcar-platform: admin` so the API picks the right cookie pair.
 */

export const AUTH_PLATFORM_HEADER = "x-softcar-platform";
export const AUTH_PLATFORM_ADMIN = "admin";

/** Cookie names (HttpOnly, set by API — not readable from JS). */
export const SOFTCAR_ADMIN_ACCESS_TOKEN_COOKIE = "softcar_admin_accessToken";
export const SOFTCAR_ADMIN_REFRESH_TOKEN_COOKIE = "softcar_admin_refreshToken";

/**
 * Production builds call the API on this site's own origin; Netlify proxies `/api/*` to
 * `REACT_APP_BACKEND` (rule written by `scripts/write-api-proxy-redirects.js`). The auth cookies are
 * then first-party — Safari blocks them when the API is called cross-site, logging users out.
 * `npm run dev` keeps calling `REACT_APP_BACKEND` directly. Opt out with `REACT_APP_API_PROXY=false`.
 */
export const IS_API_PROXY_ENABLED =
    // eslint-disable-next-line no-undef
    process.env.NODE_ENV === "production" && process.env.REACT_APP_API_PROXY !== "false";

/** Tells the API the request came through the same-origin proxy (backend `API_PROXY_HEADER`). */
export const API_PROXY_HEADER = "x-softcar-api-proxy";

export const ADMIN_AUTH_API_HEADERS = {
    [AUTH_PLATFORM_HEADER]: AUTH_PLATFORM_ADMIN,
    ...(IS_API_PROXY_ENABLED ? { [API_PROXY_HEADER]: "1" } : {}),
};

/** @param {import('axios').InternalAxiosRequestConfig} config */
export const applyAdminAuthHeaders = (config) => {
    // eslint-disable-next-line no-param-reassign
    config.headers = config.headers ?? {};
    Object.assign(config.headers, ADMIN_AUTH_API_HEADERS);
    return config;
};

export const getApiBaseUrl = () =>
    // eslint-disable-next-line no-undef
    IS_API_PROXY_ENABLED ? "/" : process.env.REACT_APP_BACKEND;

export const getAdminApiClientConfig = () => ({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
    headers: { ...ADMIN_AUTH_API_HEADERS },
});
